<template>
  <main class="min-h-screen bg-white" v-if="info">

    <!-- Key Features -->
    <template v-if="info.id == 3">
      <HeaderImage :title="info.title" :image="info.image" />
      <section class="py-10" ref="featuresSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p class="text-lg leading-relaxed mb-4 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.1s" v-html="info.desc"></p>

          <!-- dd flip cards -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div
              v-for="(item, index) in info.list"
              :key="'item'+index"
              class="relative perspective"
              :class="{ 'animate-slideInUp': featuresVisible }"
              :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.2) + 's' } : {}"
              @click="toggleFlip(index)"
            >
              <!-- Inner container with fixed height -->
              <div
                :class="['flip-card-inner', { flipped: flipStates[index] }]"
                class="h-[500px] w-full"
              >
                <!-- Front side -->
                <div class="flip-card-front rounded-xl overflow-hidden cursor-pointer">
                  <img :src="require(`@/assets/dd${index+1}.png`)" class="w-full h-full object-cover" />
                  <div class="absolute inset-0 flex items-end justify-center p-4">
                    <div>
                      <div class="w-full bg-black bg-opacity-50 text-white px-3 py-1 rounded text-2xl text-center">
                        {{ item.title }}
                      </div>
                      <div class="w-full">
                        <svg class="flex-shrink-0 w-7 h-7 text-white m-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m16 10 3-3m0 0-3-3m3 3H5v3m3 4-3 3m0 0 3 3m-3-3h14v-3"></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Back side -->
                <div class="flip-card-back rounded-xl p-6 flex flex-col justify-center items-center text-justify bg-white border rounded-lg shadow-lg cursor-pointer">
                  <p class="mb-4" v-html="item.desc"></p>
                  <!-- Stop click propagation so the button doesn't flip the card back -->
                  <router-link to="/contact" class="bg-blue-900 text-white p-3 rounded-lg" @click.stop>
                    Contact Us
                  </router-link>
                  <svg class="flex-shrink-0 w-7 h-7 m-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m16 10 3-3m0 0-3-3m3 3H5v3m3 4-3 3m0 0 3 3m-3-3h14v-3"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Lifecycle & Approach -->
      <section class="py-16 bg-gray-50" ref="lifecycleSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="lg:grid lg:grid-cols-3 lg:gap-12">
            <div v-for="(item, index) in info.list2" :key="'item--'+index" :class="{ 'animate-slideInUp': lifecycleVisible }" :style="lifecycleVisible ? { 'animation-delay': (index * 0.15) + 's' } : {}">
              <h2 class="text-lg font-bold mb-2">
                {{ item.title }}
              </h2>
              <ul class="space-y-2">
                <li v-for="stage in item.list" :key="stage" class="flex items-start">
                  <svg class="flex-shrink-0 w-6 h-6 text-red-600 mt-1.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m2.707 14.293 5.586-5.586a1 1 0 0 0 0-1.414L2.707 1.707A1 1 0 0 0 1 2.414v11.172a1 1 0 0 0 1.707.707Z"></path>
                  </svg>
                  <span class="text-lg">{{ stage }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Key Features -->
    <template v-if="info.id == 4">
      <HeaderImage :title="info.title" :video="info.video" :desc="info.short_desc2" />
      <section class="py-10" ref="featuresSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p class="text-lg leading-relaxed mb-8 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" v-html="info.desc"></p>

          <div class="grid md:grid-cols-2 items-center gap-4 justify-center mb-5">
            <div class="grid grid-cols-2 gap-6">
              <div 
                v-for="(item, index) in info.list2" 
                :key="'item'+index"
                class="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:border-red-200 transition-all duration-300" :class="{ 'animate-slideInUp': featuresVisible }" :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.2) + 's' } : {}"
              >
                <div class="inline-flex items-center w-16 h-16 bg-red-100 rounded-full mb-6 justify-center font-bold text-2xl text-red-600">
                  {{ index+1 }}
                </div>
                <h3 class="text-xl font-bold mb-2">{{ item.title }}</h3>
                <p class="text-justify" v-html="item.desc"></p>
              </div>
            </div>
            <img :src="require('@/assets/s4_1.png')" class="w-full rounded-lg" />
          </div>
          <p class="text-xl mt-10 " :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.1s">{{ info.desc2 }}</p>
          <p class="text-xl font-bold mb-2 mt-10" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.1s">{{ info.title2 }}</p>
          <div class="grid sm:grid-cols-3 gap-6">
            <div 
              v-for="(item, index) in info.list" 
              :key="'item'+index"
              class="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:border-red-200 transition-all duration-300" :class="{ 'animate-slideInUp': featuresVisible }" :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.2) + 's' } : {}"
            >
              <div class="inline-flex items-center w-16 h-16 bg-red-100 rounded-full mb-6 justify-center font-bold text-2xl text-red-600">
                {{ index+1 }}
              </div>
              <h3 class="text-xl font-bold mb-2">{{ item.title }}</h3>
              <p class="text-justify mb-10" v-html="item.desc"></p>
              <div class="flex justify-center">
                <router-link to="/contact" class="bg-blue-900 text-white p-3 rounded-lg absolute bottom-[-20px]">
                  Contact Us
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Key Features -->
    <template v-if="info.id == 5">
      <HeaderImage :title="info.title" :image="info.image" :desc="info.title2" />
      <section class="py-10" ref="featuresSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p class="text-lg leading-relaxed mb-8 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.1s" v-html="info.desc"></p>
          <p class="text-xl font-bold mb-2" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.2s">{{ info.title3 }}</p>
          <p class="text-lg leading-relaxed mb-8 text-justify" :class="{ 'animate-slideInUp': featuresVisible }" style="animation-delay: 0.3s" v-html="info.desc3"></p>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div 
              v-for="(item, index) in info.list" 
              :key="'item'+index"
              class="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:border-red-200 transition-all duration-300" :class="{ 'animate-slideInUp': featuresVisible }" :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.4) + 's' } : {}"
            >
              <div class="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" :d="item.icon"></path>
                </svg>
              </div>
              <h3 class="text-xl font-bold mb-2">{{ item.title }}</h3>
              <p class="">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Technology & Assurance -->
      <section class="py-10" ref="techSection">
        <div class="max-w-5xl mx-auto px-4">
          <h2 class="text-3xl md:text-4xl font-bold mb-8 text-center" :class="{ 'animate-slideInUp': techVisible }">
            {{ info.title4 }}
          </h2>
          <p class="text-lg leading-relaxed mb-8 text-justify" :class="{ 'animate-slideInUp': techVisible }" style="animation-delay: 0.1s" v-html="info.desc4"></p>
          <div class="mt-12 grid grid-cols-1 sm:grid-cols-3 justify-center gap-6">
            <div class="" v-for="(item, index) in info.list2" :key="'item-'+index" :class="{ 'animate-slideInUp': techVisible }" :style="techVisible ? { 'animation-delay': (index * 0.15 + 0.2) + 's' } : {}">
              <div class="flex">
                <svg class="w-8 h-8 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="item.icon"></path>
                </svg>
                <span class="text-lg font-semibold ">{{ item.title }}</span>
              </div>
              <span class="text-md text-justify">{{ item.desc }}</span>
            </div>
          </div>
        </div>
      </section>
      <section section class="py-10" ref="detailsSection">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="md:flex items-center gap-4 justify-center mb-10">
            <img :src="require('@/assets/s5_1.png')" class="h-[300px] rounded-lg shadow-lg" />
            <p class="text-2xl leading-relaxed mb-2 text-justify w-[300px] font-bold border-y-4 border-red-500 py-6 italic" :class="{ 'animate-slideInUp': detailsVisible }" style="animation-delay: 0.1s" v-html="info.desc6"></p>
          </div>
          <ul class="grid md:grid-cols-2 gap-4 items-center">
            <li
              v-for="(item, index) in info.list3" 
              :key="'item1-'+index"
              :class="{ 'animate-slideInUp': detailsVisible }" :style="detailsVisible ? { 'animation-delay': (index * 0.1 + 0.2) + 's' } : {}">
              <div class="mb-3">
                  <div class="flex items-center">
                    <svg class="w-8 h-8 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 11.917 9.724 16.5 19 7.5"></path>
                    </svg>
                    <h5 class="text-lg font-bold mb-0">{{ item.title }}</h5>
                  </div>
                  <p>{{ item.desc }}</p>
              </div>
            </li>
          </ul>
        </div>
      </section>
    </template>
  </main>
</template>

<script>
import homeData from '@/data/home.json'
import HeaderImage from '@/components/HeaderImage.vue'
export default {
  name: 'ServicePage',
  components: {
    HeaderImage
  },
  data() {
    return {
      data: homeData,
      info: {
        list: []
      },
      flipStates: [],
      heroVisible: false,
      descriptionVisible: false,
      featuresVisible: false,
      techVisible: false,
      lifecycleVisible: false,
      detailsVisible: false,
      sectionObserver: null
    }
  },
  mounted() {
    const slug = this.$route?.params?.slug
    this.info = this.data.services.list.find(item => item.title.replace(/\s+/g, '-').toLowerCase() === slug) || null
    // Show hero immediately
    this.heroVisible = true
    // Set up observer for other sections
    this.$nextTick(() => {
      this.setupSectionObserver()
    })
  },
  beforeUnmount() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect()
    }
  },
  watch: {
    'info.list': {
      handler(newList) {
        if (newList && newList.length) {
          // Initialize all cards as not flipped
          this.flipStates = new Array(newList.length).fill(false);
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleFlip(index) {
      // Toggle the flip state at the given index
      this.flipStates.splice(index, 1, !this.flipStates[index]);
    },
    setupSectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.heroVisible = true
        this.descriptionVisible = true
        this.featuresVisible = true
        this.techVisible = true
        this.lifecycleVisible = true
        this.detailsVisible = true
        return
      }
      
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const ref = entry.target.getAttribute('data-section')
              if (ref === 'hero') this.heroVisible = true
              if (ref === 'description') this.descriptionVisible = true
              if (ref === 'features') this.featuresVisible = true
              if (ref === 'tech') this.techVisible = true
              if (ref === 'lifecycle') this.lifecycleVisible = true
              if (ref === 'details') this.detailsVisible = true
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      
      if (this.$refs.heroSection) {
        this.$refs.heroSection.setAttribute('data-section', 'hero')
        this.sectionObserver.observe(this.$refs.heroSection)
      }
      if (this.$refs.descriptionSection) {
        this.$refs.descriptionSection.setAttribute('data-section', 'description')
        this.sectionObserver.observe(this.$refs.descriptionSection)
      }
      if (this.$refs.featuresSection) {
        this.$refs.featuresSection.setAttribute('data-section', 'features')
        this.sectionObserver.observe(this.$refs.featuresSection)
      }
      if (this.$refs.techSection) {
        this.$refs.techSection.setAttribute('data-section', 'tech')
        this.sectionObserver.observe(this.$refs.techSection)
      }
      if (this.$refs.lifecycleSection) {
        this.$refs.lifecycleSection.setAttribute('data-section', 'lifecycle')
        this.sectionObserver.observe(this.$refs.lifecycleSection)
      }
      if (this.$refs.detailsSection) {
        this.$refs.detailsSection.setAttribute('data-section', 'details')
        this.sectionObserver.observe(this.$refs.detailsSection)
      }
    }
  }
}
</script>

<style scoped>
.prose {
  color: #374151;
}
.prose p {
  margin-bottom: 2rem;
}

/* Fade In Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-in-out both;
}

/* Fade In Up Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out both;
}

/* Slide In Up Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideInUp {
  animation: slideInUp 0.8s ease-out both;
}

.perspective {
  perspective: 1000px;
}

.flip-card-inner {
  position: relative;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  /* height is now set via Tailwind (h-64) */
}

.flip-card-inner.flipped {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 0.75rem; /* matches rounded-xl */
}

.flip-card-back {
  transform: rotateY(180deg);
}
</style>